import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

import { getMeCached } from '../authCache';
import { fetchClasses, fetchResults } from './api/starGardenApi';

function HorizontalBars({ items, maxValue, valueLabel = '⭐' }) {
  const max = Math.max(1, Number(maxValue || 0), ...items.map((i) => Number(i.value || 0)));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {items.map((it) => {
        const v = Number(it.value || 0);
        const pct = Math.max(0, Math.min(100, (v / max) * 100));
        const isZero = v <= 0;
        return (
          <Box
            key={it.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              opacity: isZero ? 0.55 : 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                width: { xs: 140, sm: 180, md: 220 },
                flexShrink: 0,
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={it.label}
            >
              {it.label}
            </Typography>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  height: 12,
                  borderRadius: 999,
                  bgcolor: 'action.hover',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${pct}%`,
                    bgcolor: 'primary.main',
                    borderRadius: 999,
                    transition: 'width 200ms ease',
                  }}
                />
              </Box>
            </Box>

            <Typography variant="body2" sx={{ width: 56, textAlign: 'right', fontWeight: 800 }}>
              {v}{valueLabel}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

const StarGardenResultsPage = () => {
  const navigate = useNavigate();

  const [schoolId, setSchoolId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');

  const [results, setResults] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load auth/school
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await getMeCached();
        if (cancelled) return;
        if (!me?.ok || !me.data?.admin_user?.school_id) {
          setError('Oturum bilgisi alınamadı.');
          setLoading(false);
          return;
        }
        setSchoolId(me.data.admin_user.school_id);
      } catch (e) {
        if (!cancelled) {
          setError('Oturum bilgisi alınamadı.');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load classes
  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const list = await fetchClasses(schoolId);
        if (cancelled) return;
        const sorted = (list || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr-TR'));
        setClasses(sorted);
        if (sorted.length === 1) {
          setSelectedClassId(String(sorted[0].id));
        }
      } catch (e) {
        if (!cancelled) setError('Sınıflar yüklenemedi.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  const loadResults = useCallback(async () => {
    if (!schoolId || !selectedClassId) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetchResults(schoolId, selectedClassId);
      setResults(res);
    } catch (e) {
      setError('Sonuçlar yüklenemedi: ' + (e?.message || ''));
    } finally {
      setLoading(false);
    }
  }, [schoolId, selectedClassId]);

  useEffect(() => {
    if (selectedClassId) loadResults();
  }, [selectedClassId, loadResults]);

  const classNameById = useMemo(() => {
    const map = {};
    (classes || []).forEach((c) => {
      map[String(c.id)] = c.name;
    });
    return map;
  }, [classes]);

  const classTotalsList = useMemo(() => {
    const totals = (results?.class_totals || []).reduce((acc, t) => {
      acc[String(t.class_id)] = Number(t.total_stars || 0);
      return acc;
    }, {});

    return (classes || []).map((c) => ({
      key: String(c.id),
      label: c.name,
      value: totals[String(c.id)] || 0,
    }));
  }, [classes, results]);

  const criteriaList = useMemo(() => {
    return (results?.criteria || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.id ?? 0) - (b.id ?? 0));
  }, [results]);

  const studentsForDropdown = useMemo(() => {
    return (results?.students || []).slice().sort((a, b) => (
      `${a.first_name || ''} ${a.last_name || ''}`.localeCompare(
        `${b.first_name || ''} ${b.last_name || ''}`,
        'tr-TR',
        { sensitivity: 'base' },
      ) || Number(a.student_id || 0) - Number(b.student_id || 0)
    ));
  }, [results]);

  const filteredStudents = useMemo(() => {
    const all = results?.students || [];
    if (!selectedStudentId) return all;
    return all.filter((s) => String(s.student_id) === String(selectedStudentId));
  }, [results, selectedStudentId]);

  if (loading && !results) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default', p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }} elevation={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Yıldız Bahçem">
              <IconButton onClick={() => navigate('/admin/stargarden')} size="small">
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                Kriter Sonuçları
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Sınıf bazında toplamlar ve öğrenci kriter dağılımı
              </Typography>
            </Box>
          </Box>

          {selectedClassId ? (
            <Chip
              size="small"
              label={`📚 ${classNameById[String(selectedClassId)] || ''}`}
              sx={{ fontWeight: 700 }}
              variant="outlined"
            />
          ) : null}
        </Box>

        <Divider sx={{ my: 2 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3,
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="sg-res-class">Sınıf</InputLabel>
              <Select
                labelId="sg-res-class"
                label="Sınıf"
                value={selectedClassId}
                onChange={(e) => {
                  setSelectedClassId(String(e.target.value));
                  setSelectedStudentId('');
                  setResults(null);
                }}
              >
                {(classes || []).map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!results}>
              <InputLabel id="sg-res-student">Öğrenci (opsiyonel)</InputLabel>
              <Select
                labelId="sg-res-student"
                label="Öğrenci (opsiyonel)"
                value={selectedStudentId}
                onChange={(e) => setSelectedStudentId(String(e.target.value))}
              >
                <MenuItem value="">Tümü</MenuItem>
                {studentsForDropdown.map((s) => (
                  <MenuItem key={s.student_id} value={String(s.student_id)}>
                    {s.student_no}. {s.first_name} {s.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Typography variant="body2" sx={{ mt: 1.25, color: 'text.secondary' }}>
            İpucu: Bir öğrenci seçerek listeyi daraltabilirsiniz.
          </Typography>
        </Paper>

        {loading && results && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Yükleniyor…</Typography>
          </Box>
        )}

        {!!results && (
          <>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5 }}>
              Tüm Sınıflar Toplam Yıldız
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <HorizontalBars items={classTotalsList} valueLabel="" />
            </Paper>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5 }}>
              Öğrenci Kriter Dağılımı
            </Typography>

            {(filteredStudents || []).length === 0 ? (
              <Alert severity="info">Bu sınıfta öğrenci bulunamadı.</Alert>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                  gap: 2,
                }}
              >
                {filteredStudents.map((s) => {
                  const fullName = `${s.first_name || ''} ${s.last_name || ''}`.trim();
                  const maxValue = criteriaList.reduce((m, c) => {
                    const cnt = Number((s.criteria_counts || {})[String(c.id)] || (s.criteria_counts || {})[c.id] || 0);
                    return Math.max(m, cnt);
                  }, 0);

                  const items = criteriaList.map((c) => {
                    const cnt = Number((s.criteria_counts || {})[String(c.id)] || (s.criteria_counts || {})[c.id] || 0);
                    const label = `${c.icon ? c.icon + ' ' : ''}${c.name}`;
                    return { key: `${s.student_id}-${c.id}`, label, value: cnt };
                  });

                  return (
                    <Paper
                      key={s.student_id}
                      sx={{ p: 2, borderRadius: 3, bgcolor: 'background.paper' }}
                      variant="outlined"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.25 }}>
                        <Typography sx={{ fontWeight: 900 }}>
                          {s.student_no}. {fullName || 'Öğrenci'}
                        </Typography>

                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Toplam: ${Number(s.total_stars || 0)}⭐`}
                          sx={{ fontWeight: 800 }}
                        />
                      </Box>

                      {criteriaList.length === 0 ? (
                        <Alert severity="info">Kriter bulunamadı.</Alert>
                      ) : (
                        <HorizontalBars items={items} maxValue={maxValue} />
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default StarGardenResultsPage;
